import { LEAVES_BG_XS, LEAVES_BG_MD } from '../config/backgrounds';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Pagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Tooltip,
  alpha
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search,
  Star,
  FilterList,
  Clear
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import favoriteService from '../services/favoriteService';
import ProviderCard from '../components/Provider/ProviderCard';

const Favorites = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const theme = useTheme();

  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  // Szűrők
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadFavorites();
  }, [isAuthenticated, page, searchTerm, selectedCategory, selectedCity]);

  const loadFavorites = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await favoriteService.getUserFavorites(page, 9);

      if (response.favorites) {
        setFavorites(response.favorites);
        setPagination(response.pagination || {});

        const categories = [...new Set(response.favorites
          .map(fav => fav.provider.category?.name)
          .filter(Boolean))];
        const cities = [...new Set(response.favorites
          .map(fav => fav.provider.city)
          .filter(Boolean))];

        setAvailableCategories(categories);
        setAvailableCities(cities);
      }
    } catch (err) {
      console.error('Kedvencek betöltése hiba:', err);
      setError('Kedvencek betöltése sikertelen');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchTerm(inputValue);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setInputValue('');
    setSelectedCategory('');
    setSelectedCity('');
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFavoriteToggle = (isFavorite) => {
    if (!isFavorite) {
      loadFavorites();
    }
  };

  // Kliens oldali szűrés a betöltött kedvenceken
  const filteredFavorites = favorites.filter(fav => {
    const p = fav.provider;
    if (searchTerm && !p.businessName?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (selectedCategory && p.category?.name !== selectedCategory) return false;
    if (selectedCity && p.city !== selectedCity) return false;
    return true;
  });

  if (!isAuthenticated) return null;

  if (loading && favorites.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Kedvencek betöltése...
        </Typography>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          backgroundImage: {
            xs: LEAVES_BG_XS,
            md: LEAVES_BG_MD,
          },
          backgroundSize: { xs: '100% auto', md: 'cover' },
          backgroundPosition: { xs: 'center top', md: 'center center' },
          backgroundRepeat: 'no-repeat',
          zIndex: -1,
          willChange: 'transform',
          transform: 'translateZ(0)',
        }
      }}
    >
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Fejléc */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontFamily: 'Playfair Display', color: theme.palette.primary.dark, fontWeight: 700 }}
          >
            Kedvenc szolgáltatók
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Itt találod az összes kedvenc szolgáltatódat ({pagination.total || favorites.length} db)
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Szűrők */}
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            p: 3,
            borderRadius: '20px',
            background: alpha(theme.palette.background.paper, 0.92),
            backdropFilter: 'blur(12px)',
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.22)}`,
            boxShadow: `0 4px 24px ${alpha(theme.palette.primary.dark, 0.08)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <FilterList sx={{ color: theme.palette.secondary.main }} />
            <Typography variant="h6" sx={{ color: theme.palette.primary.dark, fontWeight: 600 }}>
              Szűrők
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Keresés név szerint"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />

            <FormControl fullWidth>
              <InputLabel shrink>Kategória</InputLabel>
              <Select
                value={selectedCategory}
                label="Kategória"
                onChange={(e) => { setSelectedCategory(e.target.value); setPage(1); }}
                displayEmpty
                notched
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="">Összes kategória</MenuItem>
                {availableCategories.map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel shrink>Város</InputLabel>
              <Select
                value={selectedCity}
                label="Város"
                onChange={(e) => { setSelectedCity(e.target.value); setPage(1); }}
                displayEmpty
                notched
                sx={{ borderRadius: '12px' }}
              >
                <MenuItem value="">Összes város</MenuItem>
                {availableCities.map((city) => (
                  <MenuItem key={city} value={city}>{city}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              sx={{
                borderRadius: '12px',
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 4px 10px ${theme.palette.primary.main}80`,
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Keresés
            </Button>
            <Tooltip title="Szűrők törlése">
              <IconButton
                onClick={handleClearFilters}
                disabled={loading}
                sx={{
                  borderRadius: '12px',
                  border: `2px solid ${theme.palette.divider}`,
                  '&:hover': {
                    borderColor: theme.palette.secondary.main,
                    transform: 'scale(1.05)',
                  }
                }}
              >
                <Clear />
              </IconButton>
            </Tooltip>
          </Stack>
        </Paper>

        {/* Kedvencek listája */}
        {filteredFavorites.length === 0 && !loading ? (
          <Paper
            elevation={0}
            sx={{
              textAlign: 'center',
              py: 8,
              borderRadius: '20px',
              background: alpha(theme.palette.background.paper, 0.85),
              backdropFilter: 'blur(10px)',
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Star sx={{ fontSize: 64, color: theme.palette.grey[300], mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: theme.palette.primary.dark }}>
              {searchTerm || selectedCategory || selectedCity
                ? 'Nincs találat a szűrőknek megfelelően'
                : 'Még nincsenek kedvenc szolgáltatói'
              }
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              {searchTerm || selectedCategory || selectedCity
                ? 'Próbálja meg módosítani a keresési feltételeket.'
                : 'Böngéssze a szolgáltatókat és adja hozzá őket a kedvenceihez!'
              }
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              {(searchTerm || selectedCategory || selectedCity) ? (
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  sx={{
                    borderRadius: '12px',
                    borderWidth: 2,
                    '&:hover': { borderWidth: 2 },
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  }}
                >
                  Szűrők törlése
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => navigate('/szolgaltatok')}
                  sx={{
                    borderRadius: '10px',
                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  }}
                >
                  Szolgáltatók böngészése
                </Button>
              )}
            </Stack>
          </Paper>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 3,
              mb: 4,
            }}
          >
            {filteredFavorites.map((favorite, index) => (
              <ProviderCard
                key={favorite.id}
                provider={favorite.provider}
                animationDelay={index * 80}
                onCardClick={(p) => navigate(`/szolgaltato/${p.slug || p.id}`)}
                onFavoriteToggle={handleFavoriteToggle}
              />
            ))}
          </Box>
        )}

        {/* Lapozás */}
        {pagination.pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '20px',
                border: `1px solid ${theme.palette.divider}`,
                background: alpha(theme.palette.background.paper, 0.85),
              }}
            >
              <Pagination
                count={pagination.pages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '12px',
                    fontWeight: 500,
                    '&.Mui-selected': {
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      color: 'white',
                    }
                  }
                }}
              />
            </Paper>
          </Box>
        )}

        {/* Loading overlay */}
        {loading && favorites.length > 0 && (
          <Box
            sx={{
              position: 'fixed',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Favorites;
